import {
  Award,
  Review,
  StorefrontImage,
  TastingNote,
  UrgencyMessage,
  WineAttributes,
} from "../../types";
import { Box, Heading, Section, Stack, Text } from "@vinomofo/components";

import { OfferHeroGiftCard } from "../offer-hero/OfferHeroGiftCard";
import { Fragment } from "react";
import { VideoBlock } from "../video-block";
import { VinoDataOffer } from "../../types/vino-data-types";
import { richTextParserRenderer } from "../../utils/rich-text-renderer";
import styled from "styled-components";
import { OfferHeroImage } from "../offer-hero/OfferHeroImage";
import { OfferHeroImageLarge } from "../offer-hero/OfferHeroImageLarge";
import { OfferHeroImageMixedWide } from "../offer-hero/OfferHeroImageMixedWide";

const Description = styled(Text)`
  * + * {
    margin-top: 1em;
  }
  hr {
    border-top: 1px solid ${({ theme }) => theme.colors.mofoGreen};
    margin-top: 1.5em;
  }
  blockquote + blockquote {
    margin-top: 0;
  }
`;

export type OfferDetailsGiftCardProps = {
  productId: string;
  awards: Array<Award>;
  endsAt?: Date;
  description: any;
  videoUrl;
  videoCaption;
  discountPercentage: number;
  discountDollarValue: number;
  discountDisplay: string;
  price: number;
  rrp?: number;
  unitRRP?: number;
  packRRP?: number;
  showCountdown: boolean;
  showPriceDrop: boolean;
  priceDescription?: string;
  shortDescription: string;
  region: string;
  style: string;
  reviews?: Array<Review>;
  tastingNotes: Array<TastingNote>;
  volume: string;
  wineAttributes: WineAttributes;
  heroImage: StorefrontImage;
  wideHeroImage: StorefrontImage;
  relativeSizedHeroImage: StorefrontImage;
  largeHeroImage: StorefrontImage;
  showDiscountBadge: boolean;
  showAwardBadges: boolean;
  urgencyMessage?: UrgencyMessage;
  isSoldOut?: boolean;
  tagLine?: string;
  subTagLine?: string;
  name: string;
};

export const OfferDetailsGiftCard = ({
  details,
  offer,
}: {
  details: OfferDetailsGiftCardProps;
  offer: VinoDataOffer;
}): JSX.Element => {
  const { name, description, videoUrl, videoCaption } = details;

  return (
    <Fragment>
      <Box>
        <Box maxWidth={["100%", "100%", "460px"]} paddingTop={0}>
          <OfferHeroImage
            name={name}
            image={details.heroImage}
            discountPercent={details.discountPercentage}
            discountDisplay={details.discountDisplay}
            discountDollarValue={details.discountDollarValue}
            showDiscountBadge={details.showDiscountBadge}
            isAvailable={!details?.isSoldOut}
            display={["block", "none", "none"]}
          />

          <OfferHeroImageLarge
            name={name}
            image={details.largeHeroImage}
            discountPercent={details.discountPercentage}
            discountDisplay={details.discountDisplay}
            discountDollarValue={details.discountDollarValue}
            showDiscountBadge={details.showDiscountBadge}
            isAvailable={!details?.isSoldOut}
            display={["none", "block", "none"]}
          />

          <Box
            display={["none", "none", "block"]}
            style={{
              overflow: "hidden",
              position: "absolute",
              width: "100vw",
              height: 960,
              top: 35,
              zIndex: -1,
            }}
          >
            <OfferHeroImageMixedWide
              image={details.wideHeroImage}
              name={name}
            />
          </Box>
        </Box>
        <Box justifyContent="flex-end">
          <OfferHeroGiftCard details={details} offer={offer} />
        </Box>
      </Box>
      <Section py={["50px", "50px", "75px"]} backgroundColor="almostWhite">
        <Stack space={4}>
          <Heading as="h2" variant="xxl">
            Why do we love this one?
          </Heading>
          <Stack space={4}>
            <Description fontSize={["16px", "18px", "20px"]} as="div">
              {richTextParserRenderer(description)}
            </Description>
            {videoUrl && <VideoBlock url={videoUrl} caption={videoCaption} />}
          </Stack>
        </Stack>
      </Section>
    </Fragment>
  );
};
